import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useObserverStore } from "../../stores/observerStore";
import type { StarSystem } from "../../types/celestial";

const BASE_SCALE = 10;
const ARC_SEGMENTS = 64;
const ARC_COLOR = new THREE.Color("#2dd4a8");

/** Animated arcs between systems for in-transit agents */
export function TravelArcs(): React.JSX.Element | null {
	const activeTravels = useObserverStore((s) => s.activeTravels);
	const systems = useObserverStore((s) => s.systems);

	// Build planet→system lookup
	const systemLookup = useMemo(() => {
		const map = new Map<string, StarSystem>();
		for (const sys of systems) {
			map.set(sys.id, sys);
			for (const p of sys.planets) {
				map.set(p.id, sys);
			}
		}
		return map;
	}, [systems]);

	if (activeTravels.length === 0) return null;

	return (
		<group renderOrder={3}>
			{activeTravels.map((travel) => (
				<TravelArc
					key={`${travel.agentId}-${travel.departedAt}`}
					fromPlanetId={travel.fromPlanetId}
					toPlanetId={travel.toPlanetId}
					departedAt={travel.departedAt}
					arrivesAt={travel.arrivesAt}
					systemLookup={systemLookup}
				/>
			))}
		</group>
	);
}

function TravelArc({
	fromPlanetId,
	toPlanetId,
	departedAt,
	arrivesAt,
	systemLookup,
}: {
	fromPlanetId: string;
	toPlanetId: string;
	departedAt: number;
	arrivesAt: number;
	systemLookup: Map<string, StarSystem>;
}): React.JSX.Element | null {
	const dotRef = useRef<THREE.Sprite>(null);

	const fromSys = systemLookup.get(fromPlanetId);
	const toSys = systemLookup.get(toPlanetId);
	if (!fromSys || !toSys) return null;

	const fromX = fromSys.x * BASE_SCALE;
	const fromY = -(fromSys.y * BASE_SCALE);
	const toX = toSys.x * BASE_SCALE;
	const toY = -(toSys.y * BASE_SCALE);

	// Control point for the quadratic Bezier (perpendicular offset)
	const midX = (fromX + toX) / 2;
	const midY = (fromY + toY) / 2;
	const dx = toX - fromX;
	const dy = toY - fromY;
	const dist = Math.hypot(dx, dy);
	const perpX = -dy / (dist || 1);
	const perpY = dx / (dist || 1);
	const controlX = midX + perpX * dist * 0.2;
	const controlY = midY + perpY * dist * 0.2;

	// Arc path geometry
	const linePositions = useMemo(() => {
		const pos = new Float32Array((ARC_SEGMENTS + 1) * 3);
		for (let i = 0; i <= ARC_SEGMENTS; i++) {
			const t = i / ARC_SEGMENTS;
			const oneMinusT = 1 - t;
			const x = oneMinusT * oneMinusT * fromX + 2 * oneMinusT * t * controlX + t * t * toX;
			const y = oneMinusT * oneMinusT * fromY + 2 * oneMinusT * t * controlY + t * t * toY;
			pos[i * 3] = x;
			pos[i * 3 + 1] = y;
			pos[i * 3 + 2] = 0.5;
		}
		return pos;
	}, [fromX, fromY, toX, toY, controlX, controlY]);

	// Animate the traveling dot
	useFrame(() => {
		if (!dotRef.current) return;
		const now = Date.now();
		const progress = Math.min(1, Math.max(0, (now - departedAt) / (arrivesAt - departedAt)));
		const t = progress;
		const oneMinusT = 1 - t;
		const x = oneMinusT * oneMinusT * fromX + 2 * oneMinusT * t * controlX + t * t * toX;
		const y = oneMinusT * oneMinusT * fromY + 2 * oneMinusT * t * controlY + t * t * toY;
		dotRef.current.position.set(x, y, 1);
	});

	return (
		<group>
			{/* Arc line */}
			<line>
				<bufferGeometry>
					<bufferAttribute attach="attributes-position" args={[linePositions, 3]} />
				</bufferGeometry>
				<lineBasicMaterial color={ARC_COLOR} transparent opacity={0.25} depthWrite={false} />
			</line>

			{/* Traveling dot */}
			<sprite ref={dotRef} scale={[6, 6, 1]} renderOrder={4}>
				<spriteMaterial
					color={ARC_COLOR}
					transparent
					opacity={0.9}
					depthWrite={false}
					blending={THREE.AdditiveBlending}
				/>
			</sprite>
		</group>
	);
}
